/**
 * ThemeProvider - Global theme context for applying themes across the application
 * Loads theme from API and applies CSS variables
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api/v1';

// Default theme values (Luxury Ranch Modern)
const defaultTheme = {
  theme_id: "theme_luxury_ranch",
  name: "Luxury Ranch Modern",
  branding: {
    site_name: "Horse Mountain Ranch",
    site_tagline: "Homeowners Association",
  },
  typography: {
    heading_font: "Playfair Display",
    body_font: "Inter",
    accent_font: "Cormorant Garamond",
    font_scale: 1.0,
    font_weight_scale: 1.0,
  },
  colors: {
    primary_color: "#2D5A3D",
    secondary_color: "#8B7355",
    accent_color: "#C9A86C",
    background_color: "#FAF9F6",
    surface_color: "#FFFFFF",
    text_color: "#1A1A1A",
    text_muted_color: "#6B7280",
    link_color: "#2D5A3D",
    button_primary_color: "#2D5A3D",
    button_secondary_color: "#8B7355",
    status_success_color: "#10B981",
    status_warning_color: "#F59E0B",
    status_error_color: "#EF4444",
    border_color: "#E5E7EB",
  },
  backgrounds: {
    global_background_overlay_color: "#000000",
    global_background_overlay_opacity: 0.0,
    login_overlay_opacity: 0.5,
  },
  ui_components: {
    border_radius_scale: 1.0,
    shadow_scale: 1.0,
    spacing_scale: 1.0,
    icon_set: "lucide",
  },
  cinematic_view: {
    cinematic_overlay_opacity: 0.6,
    cinematic_overlay_color: "#000000",
  },
};

const ThemeContext = createContext({
  theme: defaultTheme,
  settings: null,
  loading: true,
  refreshTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

/**
 * Convert hex color to HSL for Tailwind CSS variables
 */
const hexToHSL = (hex) => {
  if (!hex) return "0 0% 0%";
  
  // Remove # if present
  hex = hex.replace("#", "");
  
  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/**
 * Apply theme to document via CSS variables
 */
const applyThemeToDocument = (theme) => {
  if (!theme) return;
  
  const root = document.documentElement;
  const colors = theme.colors || {};
  const typography = theme.typography || {};
  const uiComponents = theme.ui_components || {};
  const backgrounds = theme.backgrounds || {};
  
  // Colors - Convert to HSL for Tailwind compatibility
  root.style.setProperty("--color-primary", hexToHSL(colors.primary_color));
  root.style.setProperty("--color-primary-hex", colors.primary_color);
  root.style.setProperty("--color-secondary", hexToHSL(colors.secondary_color));
  root.style.setProperty("--color-secondary-hex", colors.secondary_color);
  root.style.setProperty("--color-accent", hexToHSL(colors.accent_color));
  root.style.setProperty("--color-accent-hex", colors.accent_color);
  root.style.setProperty("--color-background", hexToHSL(colors.background_color));
  root.style.setProperty("--color-background-hex", colors.background_color);
  root.style.setProperty("--color-surface", hexToHSL(colors.surface_color));
  root.style.setProperty("--color-surface-hex", colors.surface_color);
  root.style.setProperty("--color-text", hexToHSL(colors.text_color));
  root.style.setProperty("--color-text-hex", colors.text_color);
  root.style.setProperty("--color-text-muted", hexToHSL(colors.text_muted_color));
  root.style.setProperty("--color-link", hexToHSL(colors.link_color));
  root.style.setProperty("--color-border", hexToHSL(colors.border_color));
  root.style.setProperty("--color-success", hexToHSL(colors.status_success_color));
  root.style.setProperty("--color-warning", hexToHSL(colors.status_warning_color));
  root.style.setProperty("--color-error", hexToHSL(colors.status_error_color));
  
  // Typography
  root.style.setProperty("--font-heading", typography.heading_font || "Playfair Display");
  root.style.setProperty("--font-body", typography.body_font || "Inter");
  root.style.setProperty("--font-accent", typography.accent_font || "Cormorant Garamond");
  root.style.setProperty("--font-scale", typography.font_scale || 1);
  root.style.setProperty("--font-weight-scale", typography.font_weight_scale || 1);
  
  // UI Components
  root.style.setProperty("--radius-scale", uiComponents.border_radius_scale || 1);
  root.style.setProperty("--shadow-scale", uiComponents.shadow_scale || 1);
  root.style.setProperty("--spacing-scale", uiComponents.spacing_scale || 1);
  
  // Background
  if (backgrounds.global_background_image_url) {
    root.style.setProperty("--background-image", `url(${backgrounds.global_background_image_url})`);
  } else {
    root.style.setProperty("--background-image", "none");
  }
  root.style.setProperty("--background-overlay-color", backgrounds.global_background_overlay_color || "#000000");
  root.style.setProperty("--background-overlay-opacity", backgrounds.global_background_overlay_opacity || 0);
  
  // Cinematic View
  const cinematic = theme.cinematic_view || {};
  root.style.setProperty("--cinematic-overlay-opacity", cinematic.cinematic_overlay_opacity || 0.6);
  root.style.setProperty("--cinematic-overlay-color", cinematic.cinematic_overlay_color || "#000000");
  if (cinematic.cinematic_heading_font) {
    root.style.setProperty("--font-cinematic-heading", cinematic.cinematic_heading_font);
  }
  
  // Load Google Fonts
  loadGoogleFonts([typography.heading_font, typography.body_font, typography.accent_font].filter(Boolean));
};

/**
 * Load Google Fonts dynamically
 */
const loadGoogleFonts = (fonts) => {
  const uniqueFonts = [...new Set(fonts.filter(f => f && f !== "inherit"))];
  if (uniqueFonts.length === 0) return;
  
  // Check if fonts are already loaded
  const existingLink = document.getElementById("google-fonts-theme");
  if (existingLink) {
    existingLink.remove();
  }
  
  const fontFamilies = uniqueFonts.map(f => f.replace(/ /g, "+") + ":wght@300;400;500;600;700").join("&family=");
  const link = document.createElement("link");
  link.id = "google-fonts-theme";
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
  document.head.appendChild(link);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchTheme = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      const data = response.data;
      setSettings(data);
      
      // Use active_theme if available, otherwise use settings values
      const activeTheme = data.active_theme || {
        ...defaultTheme,
        branding: data.branding || defaultTheme.branding,
        typography: data.typography || defaultTheme.typography,
        colors: data.colors || defaultTheme.colors,
        backgrounds: data.backgrounds || defaultTheme.backgrounds,
        ui_components: data.ui_components || defaultTheme.ui_components,
        cinematic_view: data.cinematic_view || defaultTheme.cinematic_view,
      };
      
      setTheme(activeTheme);
      applyThemeToDocument(activeTheme);
    } catch (error) {
      console.error("Failed to load theme:", error);
      // Apply default theme on error
      applyThemeToDocument(defaultTheme);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);
  
  const refreshTheme = useCallback(() => {
    setLoading(true);
    fetchTheme();
  }, [fetchTheme]);
  
  return (
    <ThemeContext.Provider value={{ theme, settings, loading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
